import { calculateTotalSpent } from '../../utils/orderHistoryCalculations';
import { calculateTotalRevenue } from '../../utils/revenueHistoryCalculations';
import { filterRevenueByDateRange } from '../../utils/dateRangeFilters';
import { filterOrdersByOverallType } from '../orderhistory/utils';
import {
  comparisonLabel,
  directionOf,
  formatEuro,
  formatPct,
  percentageChange,
  previousPeriodTotal,
  readTourSnapshot,
  sideLabel,
} from './tourValues';

/**
 * Live dashboard numbers for the tour copy. See `tourValues.ts` for how `context`
 * decides which variant of a sentence is allowed to run.
 */

/**
 * Revenue KPI. `context`:
 *  - up / down / flat — there is revenue and a previous period to compare with
 *  - amount           — revenue, but nothing to compare it against (all-time view,
 *                       or a previous period with no data at all)
 *  - ''               — no revenue in this view, so the card shows nothing to describe
 */
export function getDashboardRevenueValues(): Record<string, string> {
  const snapshot = readTourSnapshot('dashboard');
  const total = calculateTotalRevenue(snapshot.revenue, snapshot.showExVatRevenue);
  if (total === 0) return { context: '' };

  const previous = previousPeriodTotal(snapshot, snapshot.baseRevenue, (rows, start, end) =>
    calculateTotalRevenue(filterRevenueByDateRange(rows, start, end), snapshot.showExVatRevenue)
  );
  const amount = formatEuro(total);
  if (previous === null) return { context: 'amount', amount };

  const change = percentageChange(total, previous);
  return {
    context: directionOf(change),
    amount,
    percent: formatPct(Math.abs(change)),
    comparison: comparisonLabel(snapshot.view, snapshot.startDate),
  };
}

/**
 * Spend KPI. The dashboard only draws the food/drinks breakdown while the overall
 * type filter is 'all', so the sentence may only name a leading side then. `context`:
 *  - split / even — both sides have spend and the card is showing the breakdown
 *  - total        — spend, but no breakdown on screen to talk about
 *  - ''           — no spend in this view
 */
export function getDashboardSpendValues(): Record<string, string> {
  const snapshot = readTourSnapshot('dashboard');
  const total = calculateTotalSpent(snapshot.orders, snapshot.showExVatOrders);
  if (total === 0) return { context: '' };

  const spent = formatEuro(total);
  const showSplit = snapshot.overallTypeFilter === 'all';
  const food = showSplit
    ? calculateTotalSpent(filterOrdersByOverallType(snapshot.dateOrders, 'food'), snapshot.showExVatOrders)
    : 0;
  const drinks = showSplit
    ? calculateTotalSpent(filterOrdersByOverallType(snapshot.dateOrders, 'drinks'), snapshot.showExVatOrders)
    : 0;
  const sided = food + drinks;
  if (!showSplit || food <= 0 || drinks <= 0 || sided <= 0) return { context: 'total', spent };

  const foodLeads = food >= drinks;
  const leaderShare = ((foodLeads ? food : drinks) / sided) * 100;
  return {
    context: Math.abs(food - drinks) / sided < 0.02 ? 'even' : 'split',
    spent,
    leader: sideLabel(foodLeads ? 'food' : 'drinks'),
    other: sideLabel(foodLeads ? 'drinks' : 'food'),
    leaderShare: formatPct(leaderShare),
  };
}
